import React, { useState } from 'react';

import logoImg from '../src/assets/logo.png'; 

export default function LinkTreeForm() {

  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>(''); 
  const [sizeType, setSizeType] = useState<string>(''); // 'UK', 'US', or 'Other'
  const [dressSize, setDressSize] = useState<string>(''); // Holds selected size or custom typed text
  const [customSize, setCustomSize] = useState<string>(''); // Track custom type input explicitly
  const [promMonth, setPromMonth] = useState<string>('');
  const [popupInterest, setPopupInterest] = useState<string>('');
  const [schoolName, setSchoolName] = useState<string>('');
  const [schoolLocation, setSchoolLocation] = useState<string>('');
  const [inspirationBase64, setInspirationBase64] = useState<string>('');
  

  const [showModal, setShowModal] = useState<boolean>(false);
  const [generatedCode, setGeneratedCode] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  const WHATSAPP_NUMBER = "447539786604";


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setInspirationBase64(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  
  const generateUniqueCode = (clientName: string, clientEmail: string): string => {
    const cleanName = clientName.trim().toUpperCase().replace(/\s+/g, '').slice(0, 4);
    const cleanInput = clientEmail.trim().toLowerCase();
    

    let hash = 0;
    for (let i = 0; i < cleanInput.length; i++) {
      hash = cleanInput.charCodeAt(i) + ((hash << 5) - hash);
    }
    
  
    const stableHex = Math.abs(hash).toString(16).toUpperCase().slice(0, 4).padStart(4, 'E');
    
    return `DRESSTEE-10-${cleanName || 'QUEEN'}-${stableHex}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Strict email registration regex definition
    const strictEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!strictEmailRegex.test(email.trim())) {
      alert("Please enter a strictly valid email address (e.g., name@example.com).");
      return;
    }

    // Determine the final dress size value based on user's selected track
    let finalDressSize = dressSize;
    if (sizeType === 'Other') {
      finalDressSize = customSize;
    }

    if (!name || !email || !sizeType || !finalDressSize || !promMonth || !popupInterest || !schoolName || !schoolLocation) {
      alert("Please fill out all required fields.");
      return;
    }

    const targetContact = email.trim().toLowerCase();
    const finalCode = generateUniqueCode(name, targetContact);

    const leadData = {
      name,
      email: targetContact,
      dressSize: finalDressSize,
      promMonth,
      popupInterest,
      schoolName,
      schoolLocation,
      discountCode: finalCode
    };

    setGeneratedCode(finalCode);
    setIsSubmitted(true);

    const whatsappMessage = encodeURIComponent(
      `Hi Dresstee! ✨\n\n` +
      `My name is ${name}.\n` +
      `I just secured my slot for Prom 2027!\n\n` +
      `• Dress Size: ${finalDressSize}\n` +
      `• Prom Month: ${promMonth}\n` +
      `• School Name: ${schoolName}\n` +
      `• School Location: ${schoolLocation}\n` +
      `• Attend Pop-up Sale Event: ${popupInterest}\n\n` +
      `My 10% discount code is: *${finalCode}*\n\n` +
      `Please lock this in for my profile. Thank you!`
    );

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;

    try {
      await fetch('./save_lead.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData)
      });
    } catch (error) {
      console.error("Database sync failed, continuing to show success state fallback.", error);
    } finally {
      window.location.href = whatsappUrl;
    }
  };

  const getGeneralWhatsAppLink = (serviceName: string) => {
    const text = encodeURIComponent(`Hi Dresstee! I'm reaching out regarding your ${serviceName} service for Prom 2027.`);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsSubmitted(false);
    setName('');
    setEmail('');
    setSizeType('');
    setDressSize('');
    setCustomSize('');
    setPromMonth('');
    setPopupInterest('');
    setSchoolName('');
    setSchoolLocation('');
    setInspirationBase64('');
    setGeneratedCode('');
    setCopied(false);
  };

  return (
    <div 
      className="container-fluid d-flex justify-content-center align-items-start py-5" 
      style={{ 
        backgroundColor: '#000000', 
        minHeight: '100vh', 
        color: '#FFFFFF',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >
      <div className="w-100" style={{ maxWidth: '580px', padding: '0 20px' }}>

        {/* Profile Header Section */}
        <div className="text-center mb-5 mt-3">
          <div 
            className="mx-auto mb-3 d-flex align-items-center justify-content-center bg-white"
            style={{ 
              width: '110px', 
              height: '110px', 
              borderRadius: '50%', 
              overflow: 'hidden',
              border: '2px solid #222'
            }}
          >
            <img 
              src={logoImg} 
              alt="Dresstee Logo" 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  parent.innerHTML = '<div style="color:#000; font-weight:bold; font-size:24px; letter-spacing:1px;">DT</div>';
                }
              }}
            />
          </div>
          
          <h1 className="h4 fw-bold mb-2" style={{ letterSpacing: '0.5px', color: '#FFFFFF' }}>Dresstee</h1>
          <p className="mx-auto" style={{ fontSize: '14px', maxWidth: '420px', lineHeight: '1.6', letterSpacing: '0.2px', color: '#A0A0A0' }}>
            Designing high-fashion statements that make you unforgettable on your big day.
          </p>
        </div>

        {/* Links Stack */}
        <div className="d-flex flex-column gap-3">
          
          {/* 1. PROM 2027 WAITLIST REGISTRATION */}
          <button 
            onClick={() => setShowModal(true)}
            className="btn w-100 d-flex align-items-center text-start border-0 py-3 px-4"
            style={{ 
              backgroundColor: '#1E1E1E', 
              color: '#FFFFFF', 
              borderRadius: '14px',
              transition: 'transform 0.2s ease, background-color 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.01)';
              e.currentTarget.style.backgroundColor = '#252525';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = '#1E1E1E';
            }}
          >
            <div className="d-flex align-items-center justify-content-center me-3" style={{ width: '44px', height: '44px', borderRadius: '8px', backgroundColor: '#FFFFFF', fontSize: '16px' }}>
              👑
            </div>
            <div className="flex-grow-1">
              <div className="fw-semibold" style={{ fontSize: '15px', color: '#FFFFFF' }}>Secure your slot prom 2027 girlies</div>
              <div style={{ fontSize: '11px', color: '#A0A0A0' }}>Join waitlist & get your instant 10% discount code</div>
            </div>
            <span style={{ color: '#666', fontSize: '18px' }}>⋮</span>
          </button>

          {/* 2. CUSTOM BOOKINGS AND ENQUIRIES */}
          <a 
            href={getGeneralWhatsAppLink("Custom Bookings and Enquiries")}
            target="_blank"
            rel="noopener noreferrer"
            className="w-100 d-flex align-items-center text-start py-3 px-4 text-decoration-none"
            style={{ backgroundColor: '#121212', color: '#E0E0E0', borderRadius: '14px', border: '1px dashed #333', transition: 'transform 0.2s' }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.01)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div className="d-flex align-items-center justify-content-center me-3" style={{ width: '44px', height: '44px', borderRadius: '8px', backgroundColor: '#1A1A1A', fontSize: '16px' }}>
              ✂️
            </div>
            <div className="flex-grow-1">
              <div className="fw-semibold" style={{ fontSize: '15px', color: '#FFFFFF' }}>Custom bookings and enquiries</div>
              <div style={{ fontSize: '11px', color: '#25D366', fontWeight: '500' }}>Coming Soon • Chat via WhatsApp</div>
            </div>
            <span style={{ color: '#444', fontSize: '18px' }}>⋮</span>
          </a>

          {/* 3. ALTERATIONS SERVICE */}
          <a 
            href={getGeneralWhatsAppLink("Alterations Service")}
            target="_blank"
            rel="noopener noreferrer"
            className="w-100 d-flex align-items-center text-start py-3 px-4 text-decoration-none"
            style={{ backgroundColor: '#121212', color: '#E0E0E0', borderRadius: '14px', border: '1px dashed #333', transition: 'transform 0.2s' }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.01)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div className="d-flex align-items-center justify-content-center me-3" style={{ width: '44px', height: '44px', borderRadius: '8px', backgroundColor: '#1A1A1A', fontSize: '16px' }}>
              🪡
            </div>
            <div className="flex-grow-1">
              <div className="fw-semibold" style={{ fontSize: '15px', color: '#FFFFFF' }}>Alterations service</div>
              <div style={{ fontSize: '11px', color: '#25D366', fontWeight: '500' }}>Coming Soon • Chat via WhatsApp</div>
            </div>
            <span style={{ color: '#444', fontSize: '18px' }}>⋮</span>
          </a>

          {/* 4. SHOP PREMADE DRESSES */}
          <a 
            href={getGeneralWhatsAppLink("Shop Premade Dresses")}
            target="_blank"
            rel="noopener noreferrer"
            className="w-100 d-flex align-items-center text-start py-3 px-4 text-decoration-none"
            style={{ backgroundColor: '#121212', color: '#E0E0E0', borderRadius: '14px', border: '1px dashed #333', transition: 'transform 0.2s' }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.01)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div className="d-flex align-items-center justify-content-center me-3" style={{ width: '44px', height: '44px', borderRadius: '8px', backgroundColor: '#1A1A1A', fontSize: '16px' }}>
              🛍️
            </div>
            <div className="flex-grow-1">
              <div className="fw-semibold" style={{ fontSize: '15px', color: '#FFFFFF' }}>Shop premade Dresses</div>
              <div style={{ fontSize: '11px', color: '#25D366', fontWeight: '500' }}>Coming Soon • Chat via WhatsApp</div>
            </div>
            <span style={{ color: '#444', fontSize: '18px' }}>⋮</span>
          </a>

        </div>
      </div>

      {/* POPUP MODAL */}
      {showModal && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)', zIndex: 1050, padding: '20px', overflowY: 'auto' }}
        >
          <div 
            className="w-100 position-relative my-auto" 
            style={{ maxWidth: '480px', backgroundColor: '#161616', borderRadius: '24px', border: '1px solid #2A2A2A', padding: '30px' }}
          >
            <button onClick={handleCloseModal} className="btn position-absolute border-0 text-white" style={{ top: '15px', right: '15px', fontSize: '20px', background: 'none', zIndex: 10 }} >✕</button>

            {!isSubmitted ? (
              <>
                <div className="text-center mb-4">
                  <span style={{ fontSize: '32px' }}>👑</span>
                  <h2 className="h4 fw-bold mt-2 text-white">Dresstee Prom 2027</h2>
                  <p className="small text-warning px-2 py-1 rounded" style={{ backgroundColor: 'rgba(255,193,7,0.1)', display: 'inline-block', fontSize: '12px' }}>
                    ⚠️ Only a limited number of dresses will be produced for 2027. Join early to secure your spot.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
                  {/* Name */}
                  <div>
                    <label className="form-label small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px', color: '#C0C0C0' }}>Full Name *</label>
                    <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="form-control text-white border-0 py-2.5 px-3" style={{ borderRadius: '10px', backgroundColor: '#222', fontSize: '14px', boxShadow: 'none', color: '#FFFFFF' }} placeholder="Type your full name..." />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="form-label small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px', color: '#C0C0C0' }}>Email Address *</label>
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="form-control text-white border-0 py-2.5 px-3" style={{ borderRadius: '10px', backgroundColor: '#222', fontSize: '14px', boxShadow: 'none', color: '#FFFFFF' }} placeholder="name@example.com" />
                  </div>

                  {/* Dress Sizing Category Selection */}
                  <div>
                    <label className="form-label small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px', color: '#C0C0C0' }}>Dress Size Preference *</label>
                    <select 
                      required 
                      value={sizeType} 
                      onChange={(e) => {
                        setSizeType(e.target.value);
                        setDressSize('');
                        setCustomSize('');
                      }} 
                      className="form-select text-white border-0 py-2.5 px-3 mb-2" 
                      style={{ borderRadius: '10px', backgroundColor: '#222', fontSize: '14px', boxShadow: 'none', color: '#FFFFFF' }}
                    >
                      <option value="" disabled hidden style={{ backgroundColor: '#222', color: '#A0A0A0' }}>Select sizing system...</option>
                      <option value="UK" style={{ backgroundColor: '#222', color: '#FFF' }}>UK Sizing</option>
                      <option value="US" style={{ backgroundColor: '#222', color: '#FFF' }}>US Sizing</option>
                      <option value="Other" style={{ backgroundColor: '#222', color: '#FFF' }}>Other (Type below)</option>
                    </select>

                    {/* Conditional Dropdown for UK Sizing */}
                    {sizeType === 'UK' && (
                      <select required value={dressSize} onChange={(e) => setDressSize(e.target.value)} className="form-select text-white border-0 py-2.5 px-3" style={{ borderRadius: '10px', backgroundColor: '#333', fontSize: '14px', color: '#FFFFFF' }}>
                        <option value="" disabled hidden>Choose UK Size...</option>
                        {['UK 4', 'UK 6', 'UK 8', 'UK 10', 'UK 12', 'UK 14', 'UK 16', 'UK 18+'].map(val => (
                          <option key={val} value={val} style={{ backgroundColor: '#222' }}>{val}</option>
                        ))}
                      </select>
                    )}

                    {/* Conditional Dropdown for US Sizing */}
                    {sizeType === 'US' && (
                      <select required value={dressSize} onChange={(e) => setDressSize(e.target.value)} className="form-select text-white border-0 py-2.5 px-3" style={{ borderRadius: '10px', backgroundColor: '#333', fontSize: '14px', color: '#FFFFFF' }}>
                        <option value="" disabled hidden>Choose US Size...</option>
                        {['US 0', 'US 2', 'US 4', 'US 6', 'US 8', 'US 10', 'US 12', 'US 14', 'US 16+'].map(val => (
                          <option key={val} value={val} style={{ backgroundColor: '#222' }}>{val}</option>
                        ))}
                      </select>
                    )}

                    {/* Conditional Text Box Placeholder for Custom Track */}
                    {sizeType === 'Other' && (
                      <input 
                        type="text" 
                        required 
                        value={customSize} 
                        onChange={(e) => setCustomSize(e.target.value)} 
                        className="form-control text-white border-0 py-2.5 px-3" 
                        style={{ borderRadius: '10px', backgroundColor: '#333', fontSize: '14px', color: '#FFFFFF' }} 
                        placeholder="Type your custom size layout here..." 
                      />
                    )}
                  </div>

                  {/* School Name */}
                  <div>
                    <label className="form-label small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px', color: '#C0C0C0' }}>School Name *</label>
                    <input type="text" required value={schoolName} onChange={(e) => setSchoolName(e.target.value)} className="form-control text-white border-0 py-2.5 px-3" style={{ borderRadius: '10px', backgroundColor: '#222', fontSize: '14px', color: '#FFFFFF' }} placeholder="Enter your school name" />
                  </div>

                  {/* School Location */}
                  <div>
                    <label className="form-label small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px', color: '#C0C0C0' }}>School Location *</label>
                    <input type="text" required value={schoolLocation} onChange={(e) => setSchoolLocation(e.target.value)} className="form-control text-white border-0 py-2.5 px-3" style={{ borderRadius: '10px', backgroundColor: '#222', fontSize: '14px', color: '#FFFFFF' }} placeholder="City / Region" />
                  </div>

                  {/* Prom Month Dropdown - January to December */}
                  <div>
                    <label className="form-label small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px', color: '#C0C0C0' }}>Prom Month for 2027 *</label>
                    <select required value={promMonth} onChange={(e) => setPromMonth(e.target.value)} className="form-select text-white border-0 py-2.5 px-3" style={{ borderRadius: '10px', backgroundColor: '#222', fontSize: '14px', boxShadow: 'none', color: '#FFFFFF' }}>
                      <option value="" disabled hidden style={{ backgroundColor: '#222', color: '#A0A0A0' }}>Select month...</option>
                      {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((month) => (
                        <option key={month} value={`${month} 2027`} style={{ backgroundColor: '#222', color: '#FFF' }}>{month} 2027</option>
                      ))}
                    </select>
                  </div>

                  {/* Pop-up Event Track Radio Buttons */}
                  <div>
                    <label className="form-label small fw-semibold text-uppercase d-block mb-2" style={{ letterSpacing: '0.5px', color: '#C0C0C0' }}>Would you attend a pop-up sale event? *</label>
                    <div className="d-flex gap-4">
                      <label className="d-flex align-items-center gap-2 style-pointer" style={{ fontSize: '14px', color: '#FFFFFF', cursor: 'pointer' }}>
                        <input type="radio" name="popup" value="Yes" required checked={popupInterest === 'Yes'} onChange={(e) => setPopupInterest(e.target.value)} className="form-check-input bg-dark border-secondary" style={{ boxShadow: 'none' }} /> Yes
                      </label>
                      <label className="d-flex align-items-center gap-2 style-pointer" style={{ fontSize: '14px', color: '#FFFFFF', cursor: 'pointer' }}>
                        <input type="radio" name="popup" value="No" required checked={popupInterest === 'No'} onChange={(e) => setPopupInterest(e.target.value)} className="form-check-input bg-dark border-secondary" style={{ boxShadow: 'none' }} /> No
                      </label>
                    </div>
                  </div>

                  {/* File Upload Option */}
                  <div>
                    <label className="form-label small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px', color: '#C0C0C0' }}>Upload inspiration (optional)</label>
                    <input type="file" accept="image/*" onChange={handleFileChange} className="form-control text-white border-0 py-2" style={{ borderRadius: '10px', backgroundColor: '#222', fontSize: '13px', boxShadow: 'none', color: '#FFFFFF' }} />
                  </div>

                  <button type="submit" className="btn btn-light w-100 py-3 fw-bold mt-2" style={{ borderRadius: '12px', fontSize: '15px' }}>
                    Get My 10% Discount Code 💬
                  </button>
                </form>
              </>
            ) : (
              /* Success Screen */
              <div className="text-center py-3">
                <span style={{ fontSize: '40px' }}>🎉</span>
                <h2 className="h4 fw-bold mt-3 text-white">You’re on the list! 🎉</h2>
                <p style={{ color: '#A0A0A0', fontSize: '14px', lineHeight: '1.5' }} className="mb-4">
                  Kindly secure the code below! This unique code will be <strong>required to claim your 10% discount</strong> when you are ready to get your prom dress.
                </p>

                <div 
                  onClick={handleCopyCode}
                  className="p-3 mb-4 rounded-3 d-flex flex-column align-items-center justify-content-center" 
                  style={{ backgroundColor: '#222', border: '1px dashed #444', cursor: 'pointer' }}
                  title="Click to copy"
                >
                  <span style={{ fontSize: '11px', color: copied ? '#25D366' : '#A0A0A0', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
                    {copied ? "✓ Copied!" : "Your 10% Code (Click to Copy)"}
                  </span>
                  <div className="font-monospace fw-bold h5 text-white mt-1 mb-0">{generatedCode}</div>
                </div>

                <p className="small text-muted mb-4">
                  We have opened WhatsApp to confirm your spot. Send the pre-filled message to lock in your priority ranking!
                </p>

                <button onClick={handleCloseModal} className="btn btn-outline-secondary text-white w-100 py-2" style={{ borderRadius: '10px' }}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}